/* BASMIRO: free gifts added with the bracelet (the app with any purchase, the 5 € gift card with the 2-unit pack;
   see snippets/bs-gift-handles and snippets/bs-gift-min-qty).
   Keeps exactly one line of each gift in the cart while it holds enough units of the main product
   for that gift (`minQtyByVariant`, default `minQty`), and removes it otherwise. It reacts to every cart change announced by the theme
   (add to cart, quantity changes, removals) and to the cart state on page load, then announces
   its own change with the same event so the cart drawer and cart bubble refresh in place.
   Config comes from window.BS_GIFT (layout/theme.liquid). Each gift is 0 € by its own price
   or by an automatic discount set up in the Shopify admin, not here. */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const config = window.BS_GIFT;
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

let running = false;
let rerun = false;
let failures = 0;
const MAX_FAILURES = 3;

// A shopper can remove the removable gifts with the bin. The variant ids of the gifts they removed are remembered
// in a cart attribute (comma-separated), so it survives reloads and the cart template can read it too. It is cleared
// when the cart stops qualifying, so a later pack gets its gifts again.
const DISMISS_ATTR = 'bs_sin_regalos';
const removable = new Set(((config && config.removableVariantIds) || []).map(String));
let dismissing = Promise.resolve();
let lastDismissed = new Set();

/** Drops the drawn (not yet real) lines of gifts the shopper removed: the cart may render them once more right after. */
function pruneRemovedPlaceholders() {
  document.querySelectorAll('.bs-gift-line--pending[data-gift-variant]').forEach((row) => {
    if (lastDismissed.has(row.dataset.giftVariant)) row.remove();
  });
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) ids.add(component.dataset.sectionId);
  });
  return Array.from(ids);
}

async function postJSON(path, body) {
  const response = await fetch(ROOT + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'cart request failed');
  return data;
}

async function getCart() {
  const response = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } });
  return response.json();
}

/** Brings every gift line to the quantity it should have. Returns { sections, cart } to re-render, or null
    (cart is null when the last request does not return the full cart, so the caller fetches it). */
async function syncOnce() {
  const cart = await getCart();
  const productUnits = cart.items
    .filter((item) => item.product_id === config.productId)
    .reduce((sum, item) => sum + item.quantity, 0);
  // Each gift has its own threshold (snippets/bs-gift-min-qty): the app comes with any purchase, the rest need the pack.
  const wantedFor = (variantId) => {
    const min = (config.minQtyByVariant && config.minQtyByVariant[variantId]) || config.minQty;
    return productUnits >= min ? 1 : 0;
  };
  const dismissedIds = new Set(String((cart.attributes && cart.attributes[DISMISS_ATTR]) || '').split(',').filter(Boolean));
  lastDismissed = dismissedIds;
  // A removed gift is forgotten once the cart no longer qualifies for it, so a later purchase gets it again.
  const stillDismissed = Array.from(dismissedIds).filter((id) => wantedFor(Number(id)) === 1);
  if (stillDismissed.length !== dismissedIds.size) {
    await postJSON('cart/update.js', { attributes: { [DISMISS_ATTR]: stillDismissed.join(',') } });
    dismissedIds.clear();
    stillDismissed.forEach((id) => dismissedIds.add(id));
  }

  const toAdd = [];
  const toChange = [];
  for (const variantId of config.giftVariantIds) {
    const wanted = wantedFor(variantId);
    const lines = cart.items.filter((item) => item.variant_id === variantId);
    if (lines.length === 0) {
      if (wanted === 1 && !dismissedIds.has(String(variantId))) toAdd.push({ id: variantId, quantity: 1 });
      continue;
    }
    // First line of this gift takes the wanted quantity; any extra lines of it are emptied.
    lines.forEach((line, index) => {
      const quantity = index === 0 ? wanted : 0;
      if (line.quantity !== quantity) toChange.push({ id: line.key, quantity });
    });
  }
  if (toAdd.length === 0 && toChange.length === 0) return null;

  // Only the last request asks for the re-rendered sections: rendering them for every step slows each one down.
  const sections = sectionIds().join(',');
  let response = null;
  for (const [index, change] of toChange.entries()) {
    const last = index === toChange.length - 1 && !toAdd.length;
    response = await postJSON('cart/change.js', last ? { ...change, sections } : change);
  }
  if (toAdd.length) {
    // Shopify lists newest lines first, so add in reverse to end up in the snippets/bs-gift-handles order.
    response = await postJSON('cart/add.js', { items: toAdd.slice().reverse(), sections });
  }
  if (!response || !response.sections) return null;
  // cart/change.js answers with the whole cart; cart/add.js only with the added lines.
  return { sections: response.sections, cart: toAdd.length ? null : response };
}

async function sync() {
  if (!config || !Array.isArray(config.giftVariantIds) || !config.giftVariantIds.length || !config.productId || failures >= MAX_FAILURES) return;
  if (running) {
    rerun = true;
    return;
  }
  running = true;
  try {
    await dismissing;
    const result = await syncOnce();
    pruneRemovedPlaceholders();
    setTimeout(pruneRemovedPlaceholders, 500);
    if (result) {
      const sections = result.sections;
      const cart = result.cart || (await getCart());
      // The gift line is already on screen (drawn by the cart template), so this refresh must not replay the
      // "row added" / cart bubble animations; the CSS for this flag lives in snippets/cart-drawer.liquid.
      document.documentElement.setAttribute('data-bs-gift-sync', '');
      setTimeout(() => document.documentElement.removeAttribute('data-bs-gift-sync'), 1500);
      const deferred = CartLinesUpdateEvent.createPromise();
      const event = new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: config.giftVariantIds.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
        promise: deferred.promise,
      });
      event.bsGift = true;
      document.dispatchEvent(event);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections, items: cart.items, itemCount: cart.item_count, source: 'bs-gift-card', didError: false },
      });
    }
    failures = 0;
  } catch (error) {
    // A one-off network hiccup is retried on the next cart change; if it keeps failing (gift card
    // sold out, unpublished…) give up for this visit so the cart keeps working normally.
    failures += 1;
    console.warn('[bs-gift-card]', error);
  } finally {
    running = false;
    if (rerun) {
      rerun = false;
      sync();
    }
  }
}

// Bin on a removable gift line. The theme's own handler would send the removal at once and re-render the cart before the
// removal is recorded, drawing the gift's placeholder back over the row mid-animation. So this takes over: the row starts
// the theme's "removing" animation right away, the removal is recorded, and only then the cart-items component removes the
// line (fetch + re-render, no animation of its own), so that re-render already leaves the gift out.
// Listens on window (capture) to run before the theme's on:click handler, a document capture listener.
window.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('.bs-gift-line--removable .cart-items__remove');
    if (!button) return;
    const row = button.closest('[data-key]');
    const component = button.closest('cart-items-component');
    const line = Number((button.getAttribute('on:click') || '').split('/').pop());
    const variantId = row ? String(row.dataset.key).split(':')[0] : '';
    if (!row || !component || !line || !removable.has(variantId) || typeof component.updateQuantity !== 'function') return;
    event.stopImmediatePropagation();
    event.preventDefault();
    if (row.classList.contains('removing')) return;

    // Resolves once the row has animated out and is gone (the trash icon's own animation is ignored).
    const rowGone = new Promise((resolve) => {
      const remove = () => {
        row.remove();
        resolve();
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return remove();
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      const onEnd = (animationEvent) => {
        if (animationEvent.target !== row) return;
        row.removeEventListener('animationend', onEnd);
        remove();
      };
      row.addEventListener('animationend', onEnd);
      setTimeout(remove, 1000);
    });

    lastDismissed.add(variantId);
    dismissing = postJSON('cart/update.js', { attributes: { [DISMISS_ATTR]: Array.from(lastDismissed).join(',') } }).catch(
      (error) => {
        console.warn('[bs-gift-card]', error);
      }
    );
    // The re-render comes after the row is gone, so it never cuts the animation short.
    Promise.all([dismissing, rowGone]).then(() => {
      component.updateQuantity({ line, quantity: 0, action: 'clear' });
    });
  },
  true
);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.bsGift) return;
  const promise = event.promise;
  if (promise && typeof promise.then === 'function') {
    promise.then(() => sync()).catch(() => {});
  } else {
    sync();
  }
});

// On page load an empty cart has nothing to sync (most visits), so it skips the cart request; `cartCount` is the
// count the page was rendered with (layout/theme.liquid).
if (!config || config.cartCount !== 0) sync();
